import { Fragment, useEffect, useState } from "react";
import { AuditSvg, CrossSvg, FilterSvg, ReloadSvg, SearchSvg } from "../../assets/svgs";
import { BadgeComponent, ButtonComponent, EmptyTableComponent, FiltersComponent, IconButtonComponent, LoaderComponent, PaginationComponent, TableComponent, ViewAuditLogsModal } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { getAllAuditLogs } from "../../redux/slices/audit-logs-slice";
import styled from "styled-components";
import { AuthPageContainer, AuthPageHead } from "../../styles/global-styles";
import moment from "moment";
import { toast } from "react-toastify";
import { actionTypeConstant, responseStatusConstant } from "../../constants/app-constants";

const ViewButton = styled.div`
    width: max-content;
    cursor: pointer;
    margin: 0 auto;
`;

const AuditLogs = () => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.loginUserReducer);
    const { loading, auditLogsData, pagination } = useSelector((state) => state.auditLogsReducer);

    const initialPagination = {
        currentPage: 0,
        pageSize: 30,
    };
    const initialOrderBy = {
        sortColumn: "responseDate",
        isAsce: false,
    };
    const initialSearch = {
        startDateTime: null,
        endDateTime: null,
        userId: '',
        applicationProcess: '',
        actionType: '',
        responseCode: '',
        currentPage: 0,
        pageSize: 30,

    };

    const initialStates = {
        data: [],
        pagination: initialPagination,
        orderBy: initialOrderBy,
        search: initialSearch,
        selected: null,
        selectedLabel: null,
        isDropdownOpen: false,
        isModalOpen: false,
        isUpdate: false,
        isView: false,
        isFiltersOpen: false,
    };

    const [states, setStates] = useState(initialStates);

    const defaultParams = { search: states.search };

    const getAllAuditLogsData = (updatedParams) => {
        if (user && updatedParams) {
            dispatch(getAllAuditLogs({ user, ...updatedParams }));
        }
    };

    const handleReload = () => {
        getAllAuditLogsData(defaultParams);
    };

    const handlePagination = (e) => {
        const params = {
            ...defaultParams,
            search: {
                ...defaultParams.search,
                currentPage: e
            }
        };
        getAllAuditLogsData(params);
    };

    const handleChangeFilters = (e) => {
        const { name, value } = e.target;

        if (name === 'responseCode' && value.length > 3) return;

        setStates((old) => ({
            ...old,
            search: { ...old.search, [name]: value },
        }));
    };

    const handleToggleFilters = () => {
        setStates((old) => ({ ...old, search: initialSearch, isFiltersOpen: !states.isFiltersOpen }));
    };

    const handleModalOpen = ({ label, body }) => {
        if (body && Object.entries(JSON.parse(body)).length > 0) {
            const dataArray = Object.entries(JSON.parse(body)).map(([key, value]) => ({
                label: key,
                value: JSON.stringify(value)
            }));
            setStates((old) => ({ ...old, isModalOpen: true, selected: dataArray, selectedLabel: label }));
        }
    };

    const handleModalClose = () => {
        setStates((old) => ({ ...old, isModalOpen: false, selected: null, selectedLabel: null }));
    };

    const handleFiltersSubmit = (e) => {
        e.preventDefault();
        const { startDateTime, endDateTime } = states.search;

        if ((startDateTime || endDateTime) && !(startDateTime && endDateTime)) {
            toast.error('From DateTime and To DateTime cannot be empty');
            return;
        }

        if (!startDateTime && !endDateTime) {
            getAllAuditLogsData({
                ...defaultParams,
                search: states.search,
            });
            return;
        }

        const startDate = moment(startDateTime);
        const endDate = moment(endDateTime);

        if (startDate.isAfter(endDate)) {
            toast.error('From DateTime cannot be greater than To DateTime');
            return;
        }

        getAllAuditLogsData({
            ...defaultParams,
            search: {
                ...defaultParams.search,
                startDateTime: startDate.format('DD-MM-YYYY HH:mm:ss'),
                endDateTime: endDate.format('DD-MM-YYYY HH:mm:ss'),
            }
        });
    };

    useEffect(() => {
        getAllAuditLogsData(defaultParams);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (auditLogsData && Array.isArray(auditLogsData)) {
            setStates((old) => ({ ...old, data: auditLogsData, pagination: pagination }));
        }
    }, [auditLogsData]); // eslint-disable-line react-hooks/exhaustive-deps

    // useEffect(() => {
    //     const handleClickOutside = (event) => {
    //         if (!event.target.closest('.action-dropdown')) {
    //             handleCloseDropdown();
    //         }
    //     };

    //     document.addEventListener('mousedown', handleClickOutside);
    //     return () => {
    //         document.removeEventListener('mousedown', handleClickOutside);
    //     };
    // }, []);

    const searchFields = [
        {
            label: 'From',
            type: 'datetime-local',
            placeholder: 'Search by datetime from',
            name: 'startDateTime',
            value: states?.search?.startDateTime || '',
        },
        {
            label: 'To',
            type: 'datetime-local',
            placeholder: 'Search by datetime to',
            name: 'endDateTime',
            value: states?.search?.endDateTime || '',
        },
        {
            label: 'User ID',
            type: 'text',
            placeholder: 'Search by user ID',
            name: 'userId',
            value: states.search.userId,
        },
        {
            label: 'Process',
            type: 'text',
            placeholder: 'Search by process',
            name: 'applicationProcess',
            value: states.search.applicationProcess,
        },
        {
            label: 'Action Type',
            type: 'text',
            placeholder: 'Search by action type',
            name: 'actionType',
            value: states.search.actionType,
        },
        {
            label: 'Response Code',
            type: 'number',
            placeholder: 'Search by response code',
            name: 'responseCode',
            value: states.search.responseCode,
        },
        {
            type: 'action',
            actions: [
                { label: 'Cancel', icon: CrossSvg, type: 'button', outline: true, action: handleToggleFilters },
                { label: 'Search', icon: SearchSvg, type: 'submit', action: () => { } }
            ],
        }
    ];

    // TABLE COLUMNS SETUP AS PER DATA
    const columns = [
        // { field: 'userId', headerName: 'User ID', width: 120 },
        {
            field: 'applicationProcess', headerName: 'Process', width: 120,
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.applicationProcess &&
                            <p style={{ width: '200px', overflowWrap: 'break-word' }}>{row?.applicationProcess}</p>
                        }
                    </>
                )
            }
        },
        { field: 'ipAddress', headerName: 'IP Address', width: 120, align: 'center' },
        {
            field: 'actionType', headerName: 'Action Type', width: 120, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.actionType &&
                            <p style={{ textTransform: 'capitalize' }}>
                                {row?.actionType?.toLowerCase()}
                            </p>
                        }
                    </>
                )
            }
        },
        {
            field: 'responseDescription', headerName: 'Response Status', width: 135, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.responseDescription &&
                            <BadgeComponent
                                label={row?.responseDescription?.toLowerCase()}
                                outline
                                type={{
                                    [responseStatusConstant?.success]: 'success',
                                    [responseStatusConstant.pending]: 'warning',
                                    [responseStatusConstant.error]: 'error'
                                }[row?.responseDescription] || 'default'}
                            />
                        }
                    </>
                )
            }
        },
        {
            field: 'responseCode',
            headerName: 'Response Code',
            width: 135,
            align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            row?.responseCode &&
                            <BadgeComponent
                                label={row?.responseCode}
                                type={{
                                    '2xx': 'success', // Success codes
                                    '3xx': 'warning', // Redirection codes
                                    '4xx': 'error',   // Client error codes
                                    '5xx': 'error',   // Server error codes
                                    '1xx': 'default', // Informational codes
                                }[`${Math.floor(row?.responseCode / 100)}xx`] || 'default'}
                            />
                        }
                    </>
                );
            },
            sortBy: (row) => row?.responseCode,
        },
        {
            field: 'oldParameters', headerName: 'Old Parameters', width: 110, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            (row?.oldParameters && row?.oldParameters !== '-' && row?.actionType === actionTypeConstant?.update) ? (
                                <ViewButton onClick={() => handleModalOpen({ label: 'Old Parameters', body: row?.oldParameters })}>
                                    <BadgeComponent label="View" size="small" outline />
                                </ViewButton>
                            ) : (
                                <p style={{ color: 'var(--border-color)' }}>---</p>
                            )
                        }
                    </>
                )
            },
            sortable: false,
        },
        {
            field: 'newParameters', headerName: 'New Parameters', width: 110, align: 'center',
            valueGetter: (row) => {
                return (
                    <>
                        {
                            (row?.newParameters && row?.newParameters !== '-' && [actionTypeConstant?.add, actionTypeConstant?.update].includes(row?.actionType)) ? (
                                <ViewButton onClick={() => handleModalOpen({ label: 'New Parameters', body: row?.newParameters })}>
                                    <BadgeComponent label="View" size="small" outline />
                                </ViewButton>
                            ) : (
                                <p style={{ color: 'var(--border-color)' }}>---</p>
                            )
                        }
                    </>
                )
            },
            sortable: false,
        },
        // {
        //     field: 'requestBody', headerName: 'Request Body', width: 120,
        //     valueGetter: (row) => {
        //         return (
        //             <>
        //                 {
        //                     row?.requestBody &&
        //                     <ViewButton onClick={() => handleModalOpen({ label: 'Request Body', body: row?.requestBody })}>
        //                         <BadgeComponent label="View" size="small" outline />
        //                     </ViewButton>
        //                 }
        //             </>
        //         )
        //     },
        //     sortable: false,
        // },
        // {
        //     field: 'responseBody', headerName: 'Response Body', width: 120,
        //     valueGetter: (row) => {
        //         return (
        //             <>
        //                 {
        //                     row?.responseBody &&
        //                     <ViewButton onClick={() => handleModalOpen({ label: 'Response Body', body: row?.responseBody })}>
        //                         <BadgeComponent label="View" size="small" outline />
        //                     </ViewButton>
        //                 }
        //             </>
        //         )
        //     },
        //     sortable: false,
        // },
        // { field: 'requestDate', headerName: 'Request Date & Time', width: 170 },
        // { field: 'responseDate', headerName: 'Response Date & Time', width: 170 },
        { field: 'requestDate', headerName: 'Log Date & Time', width: 170, align: 'center' },
        // {
        //     field: '',
        //     headerName: 'Action',
        //     sortable: false,
        //     width: 'max-content',
        //     align: 'right',
        //     valueGetter: (row) => {
        //         return (
        //             <div className="action-dropdown" style={{ position: 'relative', float: 'right' }}>
        //                 <IconButtonComponent outline={!(states.isDropdownOpen && states.selected === row)} size='small' icon={VerticalDotsSvg} onClick={() => handleSelectRow(row)} />
        //                 <DropDownComponent isOpen={states.isDropdownOpen && states.selected === row} close={handleCloseDropdown} actions={actions(row?.acsTransId)} />
        //             </div>
        //         )
        //     },
        // },
    ];

    return (
        <AuthPageContainer $heightfull>
            {/* PAGE HEAD */}
            <AuthPageHead>
                <div className="left">
                    <h1>Audit Logs</h1>
                    <IconButtonComponent icon={ReloadSvg} outline size="small" disabled={loading} onClick={handleReload} className={loading && 'box-spin'} />
                </div>

                <div className="right">
                    <ButtonComponent icon={FilterSvg} label="Filter" size="small" outline disabled={loading} onClick={handleToggleFilters} />
                </div>
            </AuthPageHead>

            {/* FILTER */}
            {
                searchFields && searchFields.length > 0 &&
                <FiltersComponent open={states.isFiltersOpen} searchFields={searchFields} handleChange={handleChangeFilters} handleSubmit={handleFiltersSubmit} />
            }

            {/* LOADING STATE */}
            {
                loading &&
                <LoaderComponent />
            }

            {/* EMPTY STATE */}
            {
                !loading && states.data && states.data.length === 0 &&
                <EmptyTableComponent text='No data found!' />
            }

            {/* PAGE CONTENT */}
            {
                !loading && states.data && states.data.length > 0 &&
                <Fragment>
                    <TableComponent rows={states.data} columns={columns} icon={AuditSvg} />
                    <PaginationComponent data={states.pagination} handleChange={(e) => handlePagination(e)} />
                </Fragment>
            }

            {/* MODAL */}
            {
                states.isModalOpen &&
                <ViewAuditLogsModal data={states?.selected} label={states?.selectedLabel} close={handleModalClose} />
            }
        </AuthPageContainer>
    );
};

export default AuditLogs;